import { useState, useEffect } from 'react';

export type UserCurrency = 'INR' | 'USD';

/**
 * Set to 'INR' or 'USD' to force a currency during development testing.
 * Set to null for normal geo-detection behaviour.
 * IMPORTANT: Must be null before committing to production.
 */
const DEV_FORCE_CURRENCY: UserCurrency | null = null;
// const DEV_FORCE_CURRENCY: UserCurrency | null = 'USD';

/**
 * Detects whether the user is in India using:
 * 1. The browser's timezone (Asia/Calcutta or Asia/Kolkata → INR)
 * 2. The browser's locale (en-IN → INR)
 *
 * Falls back to a free IP-geolocation API call if both signals are ambiguous.
 * Defaults to USD if detection fails or times out.
 */
export function useUserCurrency(): { currency: UserCurrency; isLoading: boolean } {
  const [currency, setCurrency] = useState<UserCurrency>(DEV_FORCE_CURRENCY ?? 'USD');
  const [isLoading, setIsLoading] = useState(DEV_FORCE_CURRENCY === null);

  useEffect(() => {
    // Skip geo-detection entirely when a currency is force-overridden for testing
    if (DEV_FORCE_CURRENCY !== null) return;

    let cancelled = false;

    const detect = async () => {
      // 1. Timezone check (fast, no network)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
      if (timezone === 'Asia/Calcutta' || timezone === 'Asia/Kolkata') {
        if (!cancelled) {
          setCurrency('INR');
          setIsLoading(false);
        }
        return;
      }

      // 2. Browser locale check (fast, no network)
      const locale = (navigator.language ?? '').toLowerCase();
      if (locale === 'en-in' || locale.endsWith('-in')) {
        if (!cancelled) {
          setCurrency('INR');
          setIsLoading(false);
        }
        return;
      }

      // 3. IP-based geolocation fallback (network, best-effort with 3s timeout)
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        const response = await fetch('https://ipapi.co/json/', {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data: { country_code?: string } = await response.json();
          if (!cancelled) {
            setCurrency(data.country_code === 'IN' ? 'INR' : 'USD');
          }
        }
      } catch {
        // Network failure or timeout — default to USD already set
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    detect();

    return () => {
      cancelled = true;
    };
  }, []);

  return { currency, isLoading };
}
